import type {
  CurrentIdentityResponse,
  LoginChallengeRequest,
  LoginRequest,
  LoginResponse,
} from "@webyonetim/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Navigate, Outlet, useLocation } from "react-router";

import { ApiError, authApi, clearSensitiveTransportState } from "./api";

type PendingChallenge = Extract<LoginResponse, { readonly status: "mfa_required" }>;

interface AuthContextValue {
  readonly identity: CurrentIdentityResponse | undefined;
  readonly loading: boolean;
  readonly pendingChallenge: PendingChallenge | undefined;
  readonly sessionEnded: boolean;
  readonly clearSessionEnded: () => void;
  readonly login: (input: LoginRequest) => Promise<LoginResponse>;
  readonly logout: () => Promise<void>;
  readonly submitMfa: (input: Omit<LoginChallengeRequest, "challengeId">) => Promise<LoginResponse>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const [pendingChallenge, setPendingChallenge] = useState<PendingChallenge>();
  const [sessionEnded, setSessionEnded] = useState(false);
  const wasAuthenticated = useRef(false);
  const identityQuery = useQuery({
    queryKey: ["identity"],
    queryFn: ({ signal }) => authApi.me(signal),
    retry: false,
    staleTime: 30_000,
  });
  const identity = identityQuery.data;
  useEffect(() => {
    if (identity !== undefined) wasAuthenticated.current = true;
  }, [identity]);
  useEffect(() => {
    const unauthorized = () => {
      if (wasAuthenticated.current) setSessionEnded(true);
      setPendingChallenge(undefined);
      queryClient.clear();
    };
    window.addEventListener("webyonetim:unauthorized", unauthorized);
    return () => {
      window.removeEventListener("webyonetim:unauthorized", unauthorized);
    };
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      identity,
      loading: identityQuery.isLoading,
      pendingChallenge,
      sessionEnded,
      clearSessionEnded: () => {
        setSessionEnded(false);
      },
      login: async (input) => {
        const result = await authApi.login(input);
        if (result.status === "mfa_required") setPendingChallenge(result);
        else {
          setPendingChallenge(undefined);
          setSessionEnded(false);
          await queryClient.invalidateQueries({ queryKey: ["identity"] });
        }
        return result;
      },
      logout: async () => {
        try {
          await authApi.logout();
        } finally {
          wasAuthenticated.current = false;
          setPendingChallenge(undefined);
          clearSensitiveTransportState();
          queryClient.clear();
        }
      },
      submitMfa: async (input) => {
        if (pendingChallenge === undefined) throw new Error("Giriş doğrulama işlemi bulunamadı.");
        const request = {
          ...input,
          challengeId: pendingChallenge.challengeId,
        };
        const result = await authApi.mfa(request);
        if (result.status === "authenticated") {
          setPendingChallenge(undefined);
          setSessionEnded(false);
          await queryClient.invalidateQueries({ queryKey: ["identity"] });
        }
        return result;
      },
    }),
    [identity, identityQuery.isLoading, pendingChallenge, queryClient, sessionEnded],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error("AuthProvider bulunamadı.");
  return context;
}

export function ProtectedRoute() {
  const auth = useAuth();
  const location = useLocation();
  if (auth.loading)
    return (
      <div aria-busy="true" className="auth-layout">
        Oturum doğrulanıyor…
      </div>
    );
  if (auth.identity === undefined)
    return <Navigate replace state={{ from: location.pathname }} to="/giris" />;
  return <Outlet />;
}

export function PermissionRoute({
  permission,
}: {
  readonly permission: CurrentIdentityResponse["permissions"][number];
}) {
  const auth = useAuth();
  if (auth.identity === undefined) return <Navigate replace to="/giris" />;
  if (!auth.identity.permissions.includes(permission)) return <Navigate replace to="/yetki-yok" />;
  return <Outlet />;
}

export function apiErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.problem.code === "auth.invalid_credentials")
      return "E-posta veya parola doğrulanamadı. Bilgilerinizi kontrol edip yeniden deneyin.";
    if (error.problem.code === "auth.origin_rejected")
      return "Güvenlik doğrulaması yenilenemedi. Sayfayı yenileyip işlemi tekrar başlatın.";
    return error.problem.detail ?? "İşlem tamamlanamadı. Yeniden deneyin.";
  }
  return error instanceof Error ? error.message : "İşlem tamamlanamadı. Yeniden deneyin.";
}
